import React, { useState, useEffect } from "react";
import { Container } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  FiHome,
  FiChevronRight,
  FiCheck,
  FiMail,
  FiPhone,
  FiCalendar,
  FiUser,
  FiBriefcase,
  FiAward,
  FiBookOpen,
  FiHash,
  FiFlag,
  FiGlobe,
  FiMapPin,
} from "react-icons/fi";
import { useDispatch } from "react-redux";
import { getStaff } from "../../store/slices/staff";
import { toast } from "react-hot-toast";
import { useParams } from "react-router-dom";

// Small helper so a missing value never renders as blank space
const Field = ({ icon, label, value, mono }) => (
  <div className="record-row">
    <span className="record-label">
      {icon}
      {label}
    </span>
    <strong className={`record-value ${mono ? "mono" : ""}`}>{value || "—"}</strong>
  </div>
);

export default function StaffDetails() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [staff, setStaff] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const fetchStaff = () => {
    setIsLoading(true);
    const item = { id };
    dispatch(getStaff(item))
      .unwrap()
      .then((res) => {
        const leads = res?.data[0] || {};
        setStaff(leads);
      })
      .catch(({ message }) => {
        console.error(message);
        toast.error("Failed to fetch stafflist");
        setStaff({});
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  const initials = (staff.name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  const employeeCode = staff._id
    ? `ID-${String(staff._id).slice(-6).toUpperCase()}`
    : "ID-------";

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-section">
          <div className="breadcrumb-left">
            <div className="home-icon">
              <Link to="/institute/home">
                <FiHome style={{ color: "white", marginTop: "-4px" }} />
              </Link>
            </div>
            <FiChevronRight className="separator" />
            <Link to="/institute/staffs">Staffs</Link>
            <FiChevronRight className="separator" />
            <span className="active">Staff Details</span>
          </div>
        </div>
      </div>

      <Container fluid>
        <div className={`staff-dossier ${isLoading ? "is-loading" : ""}`}>
          {/* ---------------- ID CARD / PROFILE PANEL ---------------- */}
          <aside className="id-card">
            <div className="id-card-cover">

            </div>

            <div className="id-card-body">
              <div className="id-avatar-wrap">
                {staff.image ? (
                  <img src={staff.image} className="id-avatar" alt={staff.name || "Staff"} />
                ) : (
                  <div className="id-avatar id-avatar-fallback">{initials || "?"}</div>
                )}
                <span className="id-seal" title="Verified staff record">
                  <FiCheck />
                </span>
              </div>

              <h3 className="id-name">{staff.name}</h3>
              <span className="id-dept">{staff.dept}</span>

              <div className="id-divider">
                <span />
                <span />
                <span />
              </div>

              <div className="id-quick-info">
                <div className="id-quick-row">
                  <FiMail />
                  <span>{staff.email || "—"}</span>
                </div>
                <div className="id-quick-row">
                  <FiPhone />
                  <span>{staff.mobileNo || "—"}</span>
                </div>
                <div className="id-quick-row">
                  <FiUser />
                  <span>{staff.user?.username || "—"}</span>
                </div>
              </div>
            </div>
          </aside>

          {/* ---------------- RECORD CARDS ---------------- */}
          <div className="dossier-records">
            <section className="record-card">
              <h5>
                <FiUser /> Personal Information
              </h5>
              <Field icon={<FiMail />} label="Email" value={staff.email} />
              <Field icon={<FiPhone />} label="Mobile" value={staff.mobileNo} mono />
              <Field
                icon={<FiCalendar />}
                label="Date of Birth"
                value={staff.dob ? new Date(staff.dob).toLocaleDateString("en-GB") : null}
                mono
              />
              <Field icon={<FiUser />} label="Gender" value={staff.gender} />
            </section>

            <section className="record-card">
              <h5>
                <FiBriefcase /> Professional Details
              </h5>
              <Field icon={<FiBriefcase />} label="Department" value={staff.dept} />
              <Field
                icon={<FiAward />}
                label="Experience"
                value={staff.experience ? `${staff.experience} Years` : null}
              />
              <Field icon={<FiBookOpen />} label="Education" value={staff.education} />
              <Field
                icon={<FiCalendar />}
                label="Date of Joining"
                value={staff.doj ? new Date(staff.doj).toLocaleDateString("en-GB") : null}
                mono
              />
            </section>

            <section className="record-card">
              <h5>
                <FiHash /> Identity Details
              </h5>
              <Field icon={<FiHash />} label="Aadhaar Number" value={staff.aadhaarNumber} mono />
              <Field icon={<FiFlag />} label="Religion" value={staff.religion} />
              <Field icon={<FiGlobe />} label="Nationality" value={staff.nationality} />
            </section>

            <section className="record-card">
              <h5>
                <FiMapPin /> Address
              </h5>
              <p className="record-address">{staff.address || "—"}</p>
            </section>
          </div>
        </div>
      </Container>
    </>
  );
}
